import { mediaRotationService } from '@/api/media-rotation.service';
import type {
  CreateRotationMediaDto,
  RotationMediaResponseDto,
  UpdateRotationMediaDto,
} from '@carvia/ros-client-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toValue } from 'vue';

export const MEDIA_ROTATION_QUERY_KEY = 'media-rotation';

export const useMediaRotationList = () => {
  return useQuery<RotationMediaResponseDto[]>({
    queryKey: [MEDIA_ROTATION_QUERY_KEY],
    queryFn: () => mediaRotationService.getAll(),
  });
};

export const useMediaRotationEntry = (
  id: MaybeRefOrGetter<string | undefined | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery<RotationMediaResponseDto>({
    queryKey: [MEDIA_ROTATION_QUERY_KEY, id],
    queryFn: () => mediaRotationService.getOne(toValue(id)!),
    enabled: () => toValue(enabled) && !!toValue(id),
  });
};

export const useCreateMediaRotationEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entry: CreateRotationMediaDto) => mediaRotationService.create(entry),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MEDIA_ROTATION_QUERY_KEY] }),
  });
};

export const useUpdateMediaRotationEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entry }: { id: string; entry: UpdateRotationMediaDto }) =>
      mediaRotationService.update(id, entry),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MEDIA_ROTATION_QUERY_KEY] }),
  });
};

export const useDeleteMediaRotationEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mediaRotationService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MEDIA_ROTATION_QUERY_KEY] }),
  });
};
